// Survey query/mutation hooks (surveys.rs) + step recipe (steps.rs) + search
// (surveys_search.rs).
//   GET/POST /v1/workspaces/{ws}/surveys, GET/PATCH/DELETE /v1/surveys/{id}
//   PUT /v1/surveys/{id}/steps
//   POST /v1/workspaces/{ws}/surveys/search
//
// PATCH/DELETE/steps are scoped by surveyId only on the wire, so callers pass
// workspaceId alongside for cache invalidation (same shape as templates.ts).
// Unlike templates, a survey also has a detail cache (surveyKeys.detail) —
// the survey editor reads its own steps, which the list rows (SurveySummary)
// don't carry — so patch/delete/steps all refresh both.

import type { BoolOp, IdOp } from "@pej/kore/components";
import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { http } from "./http";
import { placementKeys, surveyKeys, templateKeys } from "./keys";
import type { PageRs, SearchRq } from "./search";
import type { PlacementMode, StepKind, StepRow, Survey, SurveySearchItem, SurveySummary } from "./types";

export interface CreateSurvey {
    name: string;
    templateId?: string;
    thankYouText?: string;
    displayTitle?: string;
    displayDescription?: string;
    /** Absent takes the server's default, English. */
    language?: string;
    /** Created with the survey, in the same transaction. */
    placements?: { name: string; mode: PlacementMode }[];
}

/** Absent leaves the column, null clears it, a value sets it. Only
 * `templateId`, `thankYouText` and the display header are nullable
 * (surveys.rs PatchSurvey). */
export interface PatchSurvey {
    name?: string;
    active?: boolean;
    templateId?: string | null;
    thankYouText?: string | null;
    displayTitle?: string | null;
    displayDescription?: string | null;
    language?: string;
}

/** One step of PUT .../steps' whole-list body (steps.rs StepDefBody). An
 *  `id` keeps that persisted row (and its answers); absent means insert. */
export interface PutStep {
    id?: string;
    kind: StepKind;
    isMetric?: boolean;
    /** Absent means not required. */
    required?: boolean;
    config: Record<string, unknown>;
}

// surveys_search.rs — sort, searchBy, paging, and two filters: template and active.
export type SurveySortField = "Name" | "CreatedAt";
export type SurveySearchQueryField = "Name";
// surveys_search.rs SurveyFilters — closed on the server: an unknown key is a 400.
export interface SurveyFilters {
    /** The surveys using one template (the template page's Used by card). */
    template?: IdOp;
    /** Live or switched off (the surveys list's Status filter). */
    active?: BoolOp;
}

// Unused by any route today — the list page reads useSurveysSearch instead
// (search is the primary flow, see surveys_search.rs). Kept for the same
// non-paginated "just fetch everything" shape templates.ts's useTemplates
// still serves elsewhere, and exercised directly by tests/api/surveys.test.ts;
// surveyKeys.list stays invalidated by the mutations below against the day
// something other than search reads it.
export function useSurveys(workspaceId: string) {
    return useQuery({
        queryKey: surveyKeys.list(workspaceId),
        queryFn: () => http.get<SurveySummary[]>(`/v1/workspaces/${workspaceId}/surveys`),
        enabled: Boolean(workspaceId),
    });
}

export function useSurvey(surveyId: string) {
    return useQuery({
        queryKey: surveyKeys.detail(surveyId),
        queryFn: () => http.get<Survey>(`/v1/surveys/${surveyId}`),
        enabled: Boolean(surveyId),
    });
}

export function useCreateSurvey(workspaceId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: CreateSurvey) => http.post<Survey>(`/v1/workspaces/${workspaceId}/surveys`, { json: body }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: surveyKeys.list(workspaceId) });
            // The list page actually renders from useSurveysSearch, not
            // useSurveys — without this the new survey is stale there for a
            // full staleTime window.
            queryClient.invalidateQueries({ queryKey: surveyKeys.searchRoot(workspaceId) });
            // A survey's template is what a template's surveyCount counts.
            queryClient.invalidateQueries({ queryKey: templateKeys.list(workspaceId) });
        },
    });
}

export function usePatchSurvey(workspaceId: string, surveyId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: PatchSurvey) => http.patch<Survey>(`/v1/surveys/${surveyId}`, { json: body }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: surveyKeys.list(workspaceId) });
            queryClient.invalidateQueries({ queryKey: surveyKeys.detail(surveyId) });
            // See useCreateSurvey — the search-backed list needs its own
            // invalidation, same reason.
            queryClient.invalidateQueries({ queryKey: surveyKeys.searchRoot(workspaceId) });
            // A survey's template is what a template's surveyCount counts.
            queryClient.invalidateQueries({ queryKey: templateKeys.list(workspaceId) });
        },
    });
}

export function useDeleteSurvey(workspaceId: string, surveyId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => http.delete<void>(`/v1/surveys/${surveyId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: surveyKeys.list(workspaceId) });
            queryClient.removeQueries({ queryKey: surveyKeys.detail(surveyId) });
            // See useCreateSurvey — the search-backed list needs its own
            // invalidation, same reason.
            queryClient.invalidateQueries({ queryKey: surveyKeys.searchRoot(workspaceId) });
            // A survey's template is what a template's surveyCount counts.
            queryClient.invalidateQueries({ queryKey: templateKeys.list(workspaceId) });
        },
        // A refusal means the counts on screen are stale (a response or a
        // placement landed since load): refresh the page's and the list's,
        // awaited, so the caller reads the fresh reason once this settles.
        onError: () =>
            Promise.all([
                queryClient.invalidateQueries({ queryKey: surveyKeys.detail(surveyId) }),
                queryClient.invalidateQueries({ queryKey: placementKeys.list(surveyId) }),
                queryClient.invalidateQueries({ queryKey: surveyKeys.searchRoot(workspaceId) }),
            ]),
    });
}

export function usePutSteps(workspaceId: string, surveyId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: surveyKeys.putSteps(surveyId),
        mutationFn: (steps: PutStep[]) => http.put<StepRow[]>(`/v1/surveys/${surveyId}/steps`, { json: { steps } }),
        onSuccess: () => {
            // The list itself renders only SurveySummary (steps omitted), but a
            // survey with no steps yet vs. a configured one is worth a refresh
            // if the list ever grows a "ready" indicator.
            queryClient.invalidateQueries({ queryKey: surveyKeys.list(workspaceId) });
            // Same reasoning applied to the search-backed list — see
            // useCreateSurvey.
            queryClient.invalidateQueries({ queryKey: surveyKeys.searchRoot(workspaceId) });
            // Returned, so the mutation stays pending until survey.steps is
            // fresh: every step form carries the whole list, and an action
            // taken on the old one would PUT it back.
            return queryClient.invalidateQueries({ queryKey: surveyKeys.detail(surveyId) });
        },
    });
}

/** True while any steps PUT for the survey is in flight, refetch included. */
export function useStepsSaving(surveyId: string): boolean {
    return useIsMutating({ mutationKey: surveyKeys.putSteps(surveyId) }) > 0;
}

/** `rq: null` holds the request back (no fetch), like `useResponseSearch`. */
export function useSurveysSearch(
    workspaceId: string,
    rq: SearchRq<SurveyFilters, SurveySortField, SurveySearchQueryField> | null,
) {
    return useQuery({
        queryKey: surveyKeys.search(workspaceId, rq),
        queryFn: () =>
            http.post<PageRs<SurveySearchItem>>(`/v1/workspaces/${workspaceId}/surveys/search`, { json: rq }),
        enabled: Boolean(workspaceId) && rq !== null,
    });
}
